import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { getPrincipalObjectExportLimit, isPrincipalImportExportEnabled, isScriptabilityEnabled, isTmlObjectIdEnabled, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { ValidEdocMetadataType } from "../../../services/generated/graphql-types";
import { useDeleteRolesMutation, useGetRolesQuery, } from "../../../services/role/role-service";
import { useGetEdocLazyQuery } from "../../../services/user-group/user-group-service";
import { BasePageContext } from "../../../contexts/basepage-context";
import { getTranslatedInfoForMessageCode } from "../../../utils/error-handling/error-strings-mapping";
import { BaseComponentContainer } from '../base-component/base-component.container';
import { ListContainer } from '../list-container/list.container';
import { getErrorCode, getSelectedOnCurrentPage, isExportObjectLimitReached, onRowsSelectAction, updateRowData, } from '../util';
import { CreateEditModal } from './create-edit-modal/create-edit-modal.container';
import { DeleteConfirmationModal } from './delete-confirmation-modal/delete-confirmation-modal';
import { downloadExportedRoles } from './download-roles.util';
import styles from './roles.module.scss';
import { useDataHooks } from './useDataHook';
import { getColumnDefinitions, getPrismErrorMessage, getToastMessage, ListActionType, MODE, refreshCells, ROLE_TML_IMPORT_PATH, roleListParams, tableColumnHeaders, TranslationsKeys, } from './util';
export var ImportEPackAction;
(function (ImportEPackAction) {
    ImportEPackAction["Create"] = "CREATE";
    ImportEPackAction["Update"] = "UPDATE";
})(ImportEPackAction || (ImportEPackAction = {}));
export const RolesContainer = () => {
    const { t } = useTranslation();
    const { metadataList, navService } = BasePageContext();
    const isTmlObjectIdFlagEnabled = isTmlObjectIdEnabled();
    const currentData = useRef();
    const gridRef = useRef(null);
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorBanner, setErrorBanner] = useState(false);
    const [errorBannerMessage, setErrorBannerMessage] = useState(null);
    const [isToastAlertVisible, setIsToastAlertVisible] = useState(false);
    const [mode, setMode] = useState(null);
    const [isRefresh, setIsRefresh] = useState(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [lastOperatedPage, setLastOperatedPage] = useState(0);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [roleListInput, setRoleListInput] = useState({ ...roleListParams });
    const [selectedRowsIds, setSelectedRowsIds] = useState([]);
    const sortData = useRef({
        sortAscending: roleListParams.sortascending,
    });
    const { getEdoc: getEdocLazyQuery, data: edocData, loading: isGetEdocLoading, error: isEdocError, } = useGetEdocLazyQuery();
    const showErrorBanner = (message) => {
        setErrorBanner(true);
        setErrorBannerMessage(message);
    };
    useEffect(() => {
        if (isEdocError && !isGetEdocLoading && !edocData) {
            showErrorBanner(t(TranslationsKeys.EXPORT_LIMIT_ERROR, {
                limit: getPrincipalObjectExportLimit(),
            }));
            setIsLoading(false);
        }
        else if (edocData && !isGetEdocLoading) {
            downloadExportedRoles(edocData, false);
            setIsLoading(false);
            setSelectedRowsIds([]);
        }
    }, [edocData, isGetEdocLoading, isEdocError]);
    const noDataProps = {
        title: t(TranslationsKeys.NO_ROLES_DEFINED),
        description: '',
        img: ErrorCode.GLOBAL_DATA,
    };
    const hideErrorBanner = () => {
        setErrorBanner(false);
        setErrorBannerMessage(null);
    };
    const successBannerMode = (successMode) => {
        setMode(successMode);
        setIsToastAlertVisible(true);
        setIsRefresh(true);
        hideErrorBanner();
    };
    const { data, error, loading, isLastBatch } = useDataHooks(useGetRolesQuery, roleListInput, isRefresh, setIsLoading, setIsRefresh, hideErrorBanner);
    const { data: deleteRolesData, error: deleteRolesError, loading: deleteRolesLoading, deleteRoles, } = useDeleteRolesMutation();
    const onChangeOffset = (offset) => {
        const { sortAscending } = sortData.current;
        setRoleListInput({
            ...roleListInput,
            offset,
            sortascending: sortAscending,
        });
        setIsLoading(true);
    };
    const onSearchChange = (term) => {
        const { sortAscending } = sortData.current;
        setRoleListInput({
            ...roleListInput,
            offset: 0,
            sortascending: sortAscending,
            pattern: term,
        });
        setIsLoading(true);
    };
    const onClickAddRoleBtn = useCallback(() => {
        setIsModalOpened(true);
        setIsEditMode(false);
    }, [isModalOpened, isEditMode]);
    const onClickDeleteRoleBtn = useCallback(() => {
        hideErrorBanner();
        setIsDeleteModalOpen(true);
    }, [isDeleteModalOpen]);
    const onDownloadError = useCallback(() => {
        showErrorBanner(t(TranslationsKeys.ROLE_EXPORT_FAILURE));
    }, [selectedRowsIds]);
    const onDownloadSuccess = useCallback(() => {
        if (!isToastAlertVisible) {
            setMode(MODE.EXPORT);
            setIsToastAlertVisible(true);
            hideErrorBanner();
        }
    }, [isToastAlertVisible, selectedRowsIds]);
    const onDeleteModalClose = () => {
        setIsDeleteModalOpen(false);
    };
    const onDeleteConfirm = async () => {
        hideErrorBanner();
        setIsLoading(true);
        let ids = [currentData.current?.id];
        if (selectedRowsIds.length > 0) {
            ids = [...selectedRowsIds];
        }
        try {
            const deletedData = await deleteRoles(ids);
            if (deletedData?.data?.Roles_delete) {
                setSelectedRowsIds([]);
                successBannerMode(MODE.DELETE);
            }
        }
        catch (err) {
            const errorCode = getErrorCode(err);
            const translatedErrorInfo = getTranslatedInfoForMessageCode(errorCode);
            const errorText = t(translatedErrorInfo.summary);
            setErrorBannerMessage(errorText);
            setErrorBanner(true);
            setSelectedRowsIds([]);
            onDeleteModalClose();
            setIsLoading(false);
        }
        setIsDeleteModalOpen(false);
    };
    const exportRoles = (roleIds) => {
        setIsLoading(true);
        const payload = roleIds.map(id => ({
            id,
            type: ValidEdocMetadataType.Role,
        }));
        getEdocLazyQuery(payload, false, false, false, isTmlObjectIdFlagEnabled, isTmlObjectIdFlagEnabled);
    };
    const suppressNavigation = ({ colDef, }) => colDef.colId === tableColumnHeaders.MORE_OPTION;
    const openEditModal = useCallback((rowData) => {
        currentData.current = rowData;
        setIsModalOpened(true);
        setIsEditMode(true);
        setIsLoading(true);
    }, []);
    const onRowActionCB = ({ action, data: actionData }) => {
        currentData.current = actionData;
        if (action === ListActionType.EDIT || action === ListActionType.VIEW) {
            openEditModal(actionData);
        }
        else if (action === ListActionType.DELETE) {
            setIsDeleteModalOpen(true);
        }
        else if (action === ListActionType.EXPORT) {
            const roleIds = [actionData.id];
            exportRoles(roleIds);
        }
    };
    const onSortingChanged = (sortState) => {
        const sortingColumn = sortState.find((el) => {
            return el.sort !== 'null';
        }) || sortState[0];
        const ascending = sortingColumn ? sortingColumn.sort === 'asc' : true;
        sortData.current.sortAscending = ascending;
        setRoleListInput({
            ...roleListInput,
            offset: 0,
            sortascending: ascending,
        });
        setIsLoading(true);
    };
    const getSecondaryButton = (text, onClick, buttonClass = null) => (React.createElement(Button, { className: styles[buttonClass], text: t(TranslationsKeys[text]), onClick: onClick, type: "secondary" }));
    const getPrimaryButton = (text, onClick, buttonClass = null) => (React.createElement(Button, { className: styles[buttonClass], text: t(TranslationsKeys[text]), onClick: onClick, type: "primary" }));
    const isTmlImportExportEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const getActionBarOnSelect = useCallback(() => (React.createElement(Horizontal, { className: styles.actionHandlerWrapper },
        React.createElement(Vertical, { className: styles.gridSearch },
            React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t('cirrus.metadata.search.placeHolder'), onSearchChange: (searchTerm) => {
                    onSearchChange(searchTerm);
                }, "data-testid": "search-bar-filters" })),
        React.createElement(Horizontal, { spacing: "c" },
            isTmlImportExportEnabled &&
                getSecondaryButton('EXPORT', () => {
                    if (isExportObjectLimitReached(selectedRowsIds)) {
                        showErrorBanner(t(TranslationsKeys.EXPORT_LIMIT_ERROR, {
                            limit: getPrincipalObjectExportLimit(),
                        }));
                    }
                    else {
                        exportRoles(selectedRowsIds);
                    }
                }),
            getSecondaryButton('DELETE_ACTION_BAR', onClickDeleteRoleBtn)))), [selectedRowsIds, rowData]);
    const getActionBar = useCallback(() => (React.createElement(Horizontal, { className: styles.actionHandlerWrapper },
        React.createElement(Vertical, { className: styles.gridSearch },
            React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t('cirrus.metadata.search.placeHolder'), onSearchChange: (searchTerm) => {
                    onSearchChange(searchTerm);
                }, className: styles.searchBarContainer, "data-testid": "search-bar-filters" })),
        React.createElement(Horizontal, { spacing: "c" },
            getSecondaryButton('ADD_ROLE_BUTTON_TITLE', onClickAddRoleBtn),
            isTmlImportExportEnabled &&
                getSecondaryButton('IMPORT_ROLE', () => {
                    localStorage.setItem('allOrgsOverride', 'false');
                    window.location.href = `${window.location.origin}${ROLE_TML_IMPORT_PATH}`;
                })))), [selectedRowsIds, rowData]);
    const onRowsSelected = (rowIds) => {
        onRowsSelectAction(rowIds, data, roleListInput, lastOperatedPage, setLastOperatedPage, selectedRowsIds, setSelectedRowsIds);
    };
    useEffect(() => {
        const filterredSelectedRowIds = getSelectedOnCurrentPage(selectedRowsIds, data);
        if (gridRef.current) {
            updateRowData(rowData, filterredSelectedRowIds, setRowData);
            refreshCells(gridRef, filterredSelectedRowIds, tableColumnHeaders.MORE_OPTION);
        }
    }, [selectedRowsIds]);
    useEffect(() => {
        updateRowData(data, getSelectedOnCurrentPage(selectedRowsIds, data), setRowData);
        setIsLoading(false);
    }, [data]);
    useEffect(() => {
        if (!deleteRolesLoading && deleteRolesError) {
            const [errorCode, errorMessage] = getPrismErrorMessage(deleteRolesError);
            showErrorBanner(errorMessage);
        }
    }, [deleteRolesError, deleteRolesLoading]);
    const getColDefsWithSortLabel = () => {
        return getColumnDefinitions().map(col => {
            if (col.colId === tableColumnHeaders.ROLE_NAME) {
                return {
                    ...col,
                    sort: roleListInput.sortascending ? 'asc' : 'desc',
                };
            }
            return col;
        });
    };
    const setAgGridApi = (agGridApi) => {
        gridRef.current = agGridApi;
    };
    const listViewProps = {
        data: rowData,
        columns: getColDefsWithSortLabel(),
        options: {
            hasCheckboxes: true,
            onRowsSelected,
            selectedRowsIds,
            isRowSelectable: (params) => !params.data.readOnly,
            suppressMovable: true,
            checkboxHeaderClass: [styles.headerStyle],
            customWidth: 48,
        },
        agGridContext: {
            onRowAction: onRowActionCB,
        },
        loading,
        onSortingChanged,
        setAgGridApi,
        suppressKeyboardEvent: suppressNavigation,
    };
    const errorConfig = {
        errorBanner,
        errorBannerMessage,
        hideErrorBanner,
    };
    return (React.createElement(BaseComponentContainer, { id: "roles-page", container: "rolesContainer", title: t(TranslationsKeys.PAGE_TITLE), error: errorConfig },
        React.createElement(Vertical, null,
            React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading },
                React.createElement(Vertical, null,
                    selectedRowsIds.length
                        ? getActionBarOnSelect()
                        : getActionBar(),
                    React.createElement(ListContainer, { testId: "roles-list-page", data: rowData, listViewProps: listViewProps, noDataProps: noDataProps, offSet: roleListInput.offset, paginationSize: roleListInput.batchsize, isLastBatch: isLastBatch, onChangeOffset: onChangeOffset, headerHeight: 32, isLoading: isLoading, onKeyDown: e => {
                            const focusedCell = gridRef.current?.getFocusedCell();
                            if (e.key === 'Enter' &&
                                gridRef.current
                                    ?.getFocusedCell()
                                    ?.column?.getColId() ===
                                    tableColumnHeaders.ROLE_NAME) {
                                const rowNode = gridRef.current.getDisplayedRowAtIndex(focusedCell.rowIndex);
                                if (rowNode) {
                                    openEditModal(rowNode.data);
                                }
                            }
                        } }, metadataList)),
                isModalOpened && (React.createElement(CreateEditModal, { isModalOpened: isModalOpened, closeModal: () => {
                        setIsModalOpened(!isModalOpened);
                    }, isEditMode: isEditMode, setIsLoading: setIsLoading, successBannerMode: successBannerMode, roleId: currentData.current?.id })),
                isDeleteModalOpen && (React.createElement(DeleteConfirmationModal, { onDeleteConfirm: onDeleteConfirm, onDeleteModalClose: onDeleteModalClose, name: currentData.current?.roleName?.text || '', isMultiSelected: selectedRowsIds.length > 0, showLoader: deleteRolesLoading }))),
            React.createElement(GlobalAlert.Toast, { message: getToastMessage(mode, selectedRowsIds.length || 1), isVisible: isToastAlertVisible, onClose: () => setIsToastAlertVisible(false), autoHide: true }))));
};
export const Roles = () => React.createElement(RolesContainer, null);
//# sourceMappingURL=roles.container.js.map